import { ShootingStar } from './ShootingStar.js';
import { wishCardHTML } from '../data/wishes.js';

const SPAWN_DELAY_MIN = 6;   // seconds between stars
const SPAWN_DELAY_MAX = 14;
const FIRST_SPAWN_DELAY = 2; // seconds before the first wish appears

/**
 * WishingStarsSystem - manages the queue of wishing stars.
 *
 * One shooting star is alive in the sky at a time. Each carries a wish
 * picked at random from the uncaught pool. When the user catches one
 * (clicks the head while flying), the wish is marked caught and a new
 * star is queued for spawning a few seconds later. If the user misses
 * a star (it flies off the sky), the wish stays available - it just
 * goes back into the pool for next time.
 *
 * The system is added to the sky like any other object so its update()
 * gets called each frame.
 */
export class WishingStarsSystem {
  constructor({ wishes, sky, onCatch, isCardOpen }) {
    this.wishes = wishes;
    this.sky = sky;
    this.onCatch = onCatch;
    this.isCardOpen = isCardOpen;

    this.caughtWishes = new Set();
    this.activeStar = null;
    this.respawnTimer = 0;
    this.respawnDelay = FIRST_SPAWN_DELAY;

    this.id = 'wishing-stars-system';
  }

  setCaughtWishes(set) {
    this.caughtWishes = set;
  }

  /** Pick a random uncaught wish, or null if all caught. */
  pickNextWish() {
    const uncaught = this.wishes.filter((w) => !this.caughtWishes.has(w.id));
    if (uncaught.length === 0) return null;
    return uncaught[Math.floor(Math.random() * uncaught.length)];
  }

  spawnStar() {
    const wish = this.pickNextWish();
    if (!wish) return; // all caught

    const star = new ShootingStar({
      onClick: () => {
        if (this.onCatch) this.onCatch(wish);
      },
      isCardOpen: this.isCardOpen,
      cardHTML: wishCardHTML(wish),
      discoveryId: `wish-${wish.id}`,
    });

    this.activeStar = star;
    this.sky.add(star);
    this.sky.add(star.trail);
  }

  removeActiveStar() {
    if (!this.activeStar) return;
    this.sky.remove(this.activeStar);
    this.sky.remove(this.activeStar.trail);
    this.activeStar = null;
  }

  update(dt) {
    // Don't spawn new stars or progress timers while a card is open
    if (this.isCardOpen()) return;

    if (this.activeStar) {
      // If the star has finished its flight (or been caught), it's in
      // 'waiting' state - remove it and queue the next spawn
      if (this.activeStar.state === 'waiting') {
        this.removeActiveStar();
        this.respawnDelay = SPAWN_DELAY_MIN + Math.random() * (SPAWN_DELAY_MAX - SPAWN_DELAY_MIN);
        this.respawnTimer = 0;
      }
    } else {
      this.respawnTimer += dt;
      if (this.respawnTimer >= this.respawnDelay) {
        this.respawnTimer = 0;
        this.spawnStar();
      }
    }
  }
}
